import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Code, Zap, Users, Award, ArrowRight, CheckCircle, Target, Shield } from 'lucide-react';
import { METRICS, CONTACT, TECH_LOGOS } from '../data/mock';
import SEO from '../components/SEO';
import ContactForm from '../components/ContactForm';

const AboutPage = () => {
  return (
    <div className="min-h-screen pt-20 relative overflow-hidden">
      <SEO 
        title="About Elite Engineering | 15+ Years of Full-Stack Excellence"
        description="Meet the engineering team behind 150+ enterprise solutions. 15+ years architecting React, Node.js, DevOps, and cloud-native systems that define excellence."
        keywords="about full-stack engineers, software development team, react nodejs experts, devops consultants, cloud engineers"
        url="/about"
      />

      {/* Geometric Backgrounds */}
      <div className="fixed inset-0 pointer-events-none opacity-20 z-0">
        <div className="geometric-pattern" style={{ top: '15%', right: '5%' }} />
        <div className="line-structure" style={{ bottom: '25%', left: '10%', transform: 'rotate(30deg)' }} />
        
        {/* Decorative Bento Boxes */}
        <div className="bento-box-decoration medium floating" style={{ top: '18%', left: '7%', animationDelay: '1.5s' }} />
        <div className="bento-box-decoration small floating" style={{ bottom: '20%', right: '12%', animationDelay: '3.5s' }} />
        <div className="luxury-gradient-overlay" style={{ bottom: '15%', left: '10%', width: '320px', height: '320px' }} />
        <div className="corner-bracket bottom-right" style={{ bottom: '22%', right: '8%' }} />
      </div>

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-violet-50/50 via-purple-50/30 to-white relative">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-50/30 via-transparent to-violet-50/30 pointer-events-none" />
        <div className="container max-w-6xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-block mb-6">
              <span className="px-5 py-2 rounded-full bg-gradient-to-r from-purple-100 to-violet-100 text-[#4C3362] text-sm font-bold border-2 border-purple-200">
                {METRICS.yearsExperience} Years • {METRICS.startupsLaunched} Launches • {METRICS.customSolutions} Solutions
              </span>
            </div>
            <h1 className="display-lg mb-8">
              Architecting <span className="gradient-text">Technical Excellence</span>
            </h1>
            <p className="text-xl text-slate-700 font-medium leading-relaxed max-w-4xl mx-auto">
              A seasoned engineering collective with {METRICS.yearsExperience} years designing 
              production-grade applications. From innovative startups to established enterprises, 
              we engineer systems that exemplify scalability and reliability.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story Section - Bento Layout */}
      <section className="py-12 bg-white relative">
        <div className="container max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bento-card"
            >
              <h2 className="display-sm mb-6 gradient-text">Our Philosophy</h2>
              <div className="space-y-4 text-slate-700 leading-relaxed">
                <p>
                  Good software comes from disciplined engineering. Over the years, we’ve seen architectures shift from monoliths to microservices and deployments evolve into fully cloud-native operations. Tools change. Expectations rise. What matters is the depth of thinking behind the system.
                </p>
                <p>
                  Our approach spans the entire stack: data modeling, service design, interface behavior, CI/CD pipelines, and the cloud environments that bring everything together.
                </p>
                <p>
                  With {METRICS.startupsLaunched} successful launches and {METRICS.customSolutions} bespoke 
                  solutions across fintech, healthcare, e-commerce, and SaaS domains, each engagement has sharpened how we design, build, and refine.
                </p>
                <p className="font-semibold text-[#4C3362]">
                  We architect with purpose. We engineer with care. We partner in your success.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="bento-card bg-gradient-to-br from-purple-50 to-violet-50">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#4C3362] to-[#3C284D] flex items-center justify-center shadow-lg">
                    <Code className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-lg mb-1">Full-Stack Mastery</h3>
                    <p className="text-slate-600 text-sm">
                      React, Angular, Node.js, Python, Java—comprehensive stack expertise
                    </p>
                  </div>
                </div>
              </div>

              <div className="bento-card bg-gradient-to-br from-teal-50 to-cyan-50">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center shadow-lg" style={{ background: 'linear-gradient(135deg, #28D9C3, #20C8B4)' }}>
                    <Zap className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-lg mb-1">Cloud & DevOps Excellence</h3>
                    <p className="text-slate-600 text-sm">
                      AWS, Azure, GCP, Kubernetes, Terraform—complete automation
                    </p>
                  </div>
                </div>
              </div>

              <div className="bento-card bg-gradient-to-br from-slate-50 to-gray-50">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-slate-700 to-gray-700 flex items-center justify-center shadow-lg">
                    <Users className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-lg mb-1">Collaborative Partnership</h3>
                    <p className="text-slate-600 text-sm">
                      Seamless team extension with transparent communication
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What Defines Excellence - Mosaic Grid */}
      <section className="py-12 bg-gradient-to-b from-white to-slate-50 relative">
        <div className="container max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="display-sm mb-6 gradient-text">What Defines Excellence</h2>
            <p className="text-lg text-slate-700 max-w-2xl mx-auto">
              Distinguishing characteristics of our engineering approach
            </p>
          </motion.div>

          <div className="bento-grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Target,
                title: 'Production-First Engineering',
                desc: 'Systems architected for real traffic, authentic users, and genuine business challenges.',
                color: '#633AA0'
              },
              {
                icon: Shield,
                title: 'Zero Vendor Lock-in',
                desc: 'Clean, maintainable codebases enabling seamless team transitions and knowledge transfer.',
                color: '#28D9C3'
              },
              {
                icon: Zap,
                title: 'Velocity Without Compromise',
                desc: 'Rapid delivery through modern tooling and best practices, never compromising quality.',
                color: '#212227'
              },
              {
                icon: Code,
                title: 'Complete Technical Ownership',
                desc: 'End-to-end responsibility from architecture decisions through production deployment.',
                color: '#633AA0'
              },
              {
                icon: Users,
                title: 'Transparent Communication',
                desc: 'Continuous updates, demonstration videos, and direct engineering access.',
                color: '#28D9C3'
              },
              {
                icon: Award,
                title: 'Battle-Hardened Stack',
                desc: 'Production-proven technologies that scale. No experimental frameworks in production.',
                color: '#212227'
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bento-card"
              >
                <div 
                  className="w-14 h-14 rounded-xl mb-4 flex items-center justify-center shadow-lg"
                  style={{ 
                    background: `linear-gradient(135deg, ${item.color}20, ${item.color}10)`,
                    border: `2px solid ${item.color}30`
                  }}
                >
                  <item.icon className="w-7 h-7" style={{ color: item.color }} />
                </div>
                <h3 className="font-bold text-lg mb-2" style={{ color: item.color }}>
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Arsenal - Compact Grid */}
      <section className="py-12 bg-white relative">
        <div className="container max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="display-sm mb-6 gradient-text">Technology Arsenal</h2>
            <p className="text-lg text-slate-700">
              Tools and frameworks chosen for their stability, maturity, and ability to support enterprise-level architecture.
            </p>
          </motion.div>

          <div className="bento-grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 max-w-5xl mx-auto">
            {['react', 'nodejs', 'python', 'typescript', 'docker', 'kubernetes', 'aws', 'postgresql', 'mongodb', 'redis', 'graphql', 'nextjs'].map((tech) => (
              <motion.div
                key={tech}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="bento-card p-6 text-center group cursor-pointer"
              >
                <img src={TECH_LOGOS[tech]} alt={tech} className="w-10 h-10 mx-auto mb-2 transition-transform group-hover:scale-110" />
                <span className="text-xs font-semibold text-slate-600 capitalize">
                  {tech.replace('nodejs', 'Node.js').replace('nextjs', 'Next.js').replace('postgresql', 'PostgreSQL').replace('mongodb', 'MongoDB').replace('graphql', 'GraphQL')}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-to-br from-[#4C3362] via-[#3C284D] to-[#2E2040] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-300 rounded-full blur-3xl" />
        </div>
        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-8 leading-tight">Engineer Excellence Together</h2>
            <p className="text-xl text-white/90 mb-10 leading-relaxed">
              Transform your vision into production-grade reality through strategic engineering partnership.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link 
                to="/contact"
                className="inline-flex items-center gap-2 bg-white text-[#4C3362] font-bold px-10 py-5 rounded-xl hover:shadow-2xl hover:-translate-y-2 transition-all text-lg"
              >
                Get Started
                <ArrowRight className="w-6 h-6" />
              </Link>
              <Link 
                to="/case-studies"
                className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white font-bold px-10 py-5 rounded-xl hover:bg-white/20 transition-all text-lg border-2 border-white/30"
              >
                Explore Portfolio
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 bg-gradient-to-b from-white to-slate-50">
        <div className="container max-w-5xl">
          <ContactForm ctaText="Partner With Us on Your Next Project" />
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
